import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";

const DESKTOP_BREAKPOINT = 900;

export function Shell({ navItems, brandTitle, brandSubtitle, eyebrow, user, onSignOut, fallbackTitle = "Overview", theme = "admin" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > DESKTOP_BREAKPOINT);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > DESKTOP_BREAKPOINT && localStorage.getItem("formula_sidebar_collapsed") !== "true");
  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > DESKTOP_BREAKPOINT;
      setIsDesktop(desktop);
      setSidebarOpen(desktop ? localStorage.getItem("formula_sidebar_collapsed") !== "true" : false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const exactItem = navItems.find((item) => item.path === location.pathname);
  const activeItem = exactItem || navItems
    .filter((item) => {
      if (item.end || item.path === "/") return false;
      const basePath = item.path.replace(/\/$/, "");
      return location.pathname.startsWith(`${basePath}/`);
    })
    .sort((left, right) => right.path.length - left.path.length)[0];
  const title = activeItem?.label || fallbackTitle;

  useEffect(() => { document.title = `${title} | Formula Soccer ${eyebrow}`; }, [title, eyebrow]);
  useEffect(() => { if (!isDesktop) setSidebarOpen(false); }, [location.pathname, isDesktop]);
  useEffect(() => {
    if (isDesktop || !sidebarOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    window.requestAnimationFrame(() => sidebarRef.current?.querySelector(selector)?.focus());
    const keydown = (event) => {
      if (event.key === "Escape") { setSidebarOpen(false); menuButtonRef.current?.focus(); return; }
      if (event.key !== "Tab") return;
      const items = [...(sidebarRef.current?.querySelectorAll(selector) || [])].filter((item) => item.getClientRects().length);
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", keydown); };
  }, [isDesktop, sidebarOpen]);

  const toggleSidebar = () => setSidebarOpen((value) => { const next = !value; if (isDesktop) localStorage.setItem("formula_sidebar_collapsed", String(!next)); return next; });
  const leavePortal = () => { onSignOut(); navigate("/login", { replace: true }); };

  return (
    <div className={`ops-shell theme-${theme}`}>
      <Sidebar
        navItems={navItems}
        brandTitle={brandTitle}
        brandSubtitle={brandSubtitle}
        open={sidebarOpen}
        isDesktop={isDesktop}
        sidebarRef={sidebarRef}
        onToggle={toggleSidebar}
        onLinkClick={() => { if (!isDesktop) setSidebarOpen(false); }}
        onSignOut={leavePortal}
      />

      {!isDesktop && sidebarOpen && (
        <div className="ops-sidebar-backdrop" aria-hidden="true" onClick={() => { setSidebarOpen(false); menuButtonRef.current?.focus(); }} />
      )}

      <div className="ops-main">
        <Header title={title} eyebrow={eyebrow} user={user} onSignOut={onSignOut} menuButtonRef={menuButtonRef} onMenuClick={toggleSidebar} />
        <section className="ops-content">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
